import type { AiMagicQuickControl } from '../../aiService'
import type { AiOperationalPlan } from '../../../types/aiOperational'
import { isClarificationEnvelope, stripClarificationEnvelope } from '../clarificationEnvelope'

export type AiMagicOperationalRoute =
  | { kind: 'CONTINUE_MODEL'; escapeHint?: AiMagicQuickControl; executionPath?: 'RUN_NOW' | 'CREATE_AUTOMATION' }
  | { kind: 'BLOCK'; message: string }
  | { kind: 'CONFIRM'; message: string; quickControls: AiMagicQuickControl[]; clarificationField?: 'operational_confirmation' | 'execute_path' }
  | { kind: 'LOCAL_ANSWER'; message: string }

const EXPLAIN_ESCAPE_CHIP: AiMagicQuickControl = {
  type: 'button',
  label: 'Just explain read-only',
  value: 'Keep this read-only and explain the workspace state.',
}

export type AiMagicOperationalConfirmationResponse = 'PREPARE_PROPOSAL' | 'EXPLAIN_ONLY'
export type AiMagicExecutePathResponse = 'RUN_NOW' | 'CREATE_AUTOMATION' | 'EXPLAIN_ONLY'

const CONSENT_REPLY_REGEX =
  /^(prepare|create|draft|make|build|generate|proceed|go\s+ahead|do\s+it|sounds\s+good|let'?s\s+(do|go|start)|okay|ok|yes|sure|yeah|yep|alright|please\s+(do|prepare|make|draft|generate|proceed))\b/i

const EXPLAIN_REPLY_REGEX =
  /^(explain|explain\s+only|just\s+explain|only\s+explain|read[- ]?only|keep\s+it\s+read[- ]?only|no\s*[,!.]?\s*(just|only)?\s*explain)\b/i

const RUN_NOW_REPLY_REGEX =
  /^(do\s+(it|this)\s+now|run\s+(it|this)\s+now|run\s+now|just\s+do\s+it|do\s+it\s+once|one[- ]?time|one[- ]?off|just\s+run\s+it|run\s+it|do\s+this)\b/i

const CREATE_AUTOMATION_REPLY_REGEX =
  /^(create|make|turn|save|set\s+up)\b.*\b(automation|workflow)\b|^automate\s+(it|this)\b|^make\s+(it|this)\s+automatic\b/i

const PROJECT_ACTION_REGEX =
  /\b(plan|prepare|create|build|draft|organize|schedule|coordinate|track|monitor|brief|follow[- ]?up|book|pay|review|validate|check|finalize|assign|delegate|risk|deadline|rsvp|vendor|helper|theme|style|payment|role)\b/gi

const DETAILED_CREATE_STRUCTURE_REGEX =
  /(?:^|\n)\s*(?:[-*]|\d+[.)])\s+|[,;:]/g

export const isDetailedCreatePrompt = (
  prompt: string,
  options: { hasAttachments?: boolean } = {},
): boolean => {
  const rawText = stripClarificationEnvelope(prompt).trim()
  const text = rawText.replace(/\s+/g, ' ')
  if (!rawText) return false
  const minimumLength = options.hasAttachments ? 220 : 360
  if (text.length < minimumLength) return false
  const uniqueActionSignals = new Set(
    Array.from(text.matchAll(PROJECT_ACTION_REGEX), (match) => match[0].toLowerCase()),
  ).size
  const structureSignals = (rawText.match(DETAILED_CREATE_STRUCTURE_REGEX) ?? []).length
  const hasDateOrCadenceSignal =
    /\b(?:today|tomorrow|by|before|after|on|until|daily|weekly|monthly|week|month|year|morning|evening|\d{1,2}[/-]\d{1,2}|\d{4})\b/i.test(text)
  const hasCompletenessSignal =
    /\b(?:missing|anything else|if nothing|satisfied|enough|complete|validate|check everything|review everything|questions?)\b/i.test(text)
  const hasMultipleWorkstreams =
    uniqueActionSignals >= 5 || structureSignals >= 6
  const detailScore = [
    text.length >= 600,
    hasMultipleWorkstreams,
    hasDateOrCadenceSignal,
    hasCompletenessSignal,
    options.hasAttachments === true,
  ].filter(Boolean).length
  return detailScore >= 3
}

export const parseAiMagicOperationalConfirmationResponse = (
  text: string,
): AiMagicOperationalConfirmationResponse | null => {
  const trimmed = text.trim()
  if (trimmed.startsWith('[LINE_CLARIFICATION_RESPONSE]')) {
    const field = text.match(/^field:\s*(.+)$/m)?.[1]?.trim()
    if (field !== 'operational_confirmation') return null
    const value = text.match(/^value:\s*(.+)$/m)?.[1]?.trim() ?? ''
    if (/explain|read-only|read only/i.test(value)) return 'EXPLAIN_ONLY'
    if (/prepare|proposal|reviewable/i.test(value)) return 'PREPARE_PROPOSAL'
    return null
  }
  // Reply-shaped answers only. Longer messages are fresh prompts, not consents.
  if (trimmed.length > 80) return null
  if (EXPLAIN_REPLY_REGEX.test(trimmed)) return 'EXPLAIN_ONLY'
  if (CONSENT_REPLY_REGEX.test(trimmed)) return 'PREPARE_PROPOSAL'
  return null
}

export const parseAiMagicExecutePathResponse = (
  text: string,
): AiMagicExecutePathResponse | null => {
  const trimmed = text.trim()
  if (trimmed.startsWith('[LINE_CLARIFICATION_RESPONSE]')) {
    const field = text.match(/^field:\s*(.+)$/m)?.[1]?.trim()
    if (field !== 'execute_path') return null
    const value = text.match(/^value:\s*(.+)$/m)?.[1]?.trim() ?? ''
    if (/explain|read-only|read only/i.test(value)) return 'EXPLAIN_ONLY'
    if (/automation|workflow|automate/i.test(value)) return 'CREATE_AUTOMATION'
    if (/now|once|one-time|one time|run/i.test(value)) return 'RUN_NOW'
    return null
  }
  if (trimmed.length > 80) return null
  if (EXPLAIN_REPLY_REGEX.test(trimmed)) return 'EXPLAIN_ONLY'
  if (CREATE_AUTOMATION_REPLY_REGEX.test(trimmed)) return 'CREATE_AUTOMATION'
  if (RUN_NOW_REPLY_REGEX.test(trimmed)) return 'RUN_NOW'
  return null
}

export const hasPreparedProposalIntent = (turns: Array<{ role: string; text: string; requestText?: string }>) =>
  turns.some((turn) => turn.role === 'USER' && parseAiMagicOperationalConfirmationResponse(turn.requestText ?? turn.text) === 'PREPARE_PROPOSAL')

const TIMELINE_DRAFT_INTENT_REGEX =
  /\b(plan|prepare|make|build|create|draft|organize|schedule|timeline|roadmap|recipe|checklist|steps?|tasks?|subtasks?|goal|help\s+me|for\s+me|sketch|outline|design|structure|arrange|coordinate|lay\s+out|map\s+out|walk\s+me\s+through|figure\s+out|work\s+out)\b/i

const REVIEW_ONLY_DRAFT_REGEX = /\b(proposal|review|move to manage|manage draft|approve|approval)\b/i

const CLEAR_INTENT_LEADERS_REGEX =
  /^(help\s+me\s+(with\s+)?(a\s+)?|i\s+(want|need|would\s+like)\s+(to\s+)?(make|build|create|prepare|draft|plan|schedule|design|organize|coordinate)|make\s+me\s+(a\s+)?|build\s+me\s+(a\s+)?|draft\s+(me\s+)?(a\s+)?|prepare\s+(me\s+)?(a\s+)?|plan\s+(me\s+)?(a\s+)?|can\s+you\s+(help|make|build|prepare|plan|draft))/i

const EXECUTE_AUTOMATION_PROMPT_REGEX =
  /\b(automation|automate|workflow|save\s+.*\s+as\s+an?\s+automation|turn\s+.*\s+into\s+an?\s+automation|set\s+up\s+an?\s+automation|every\s+time|each\s+time|whenever)\b/i

const EXECUTE_RUN_NOW_PROMPT_REGEX =
  /\b(now|right\s+now|once|one[- ]?off|one[- ]?time|this\s+time|mark|complete|assign|notify|post|send|update|log)\b/i

const summarizeTargets = (plan: AiOperationalPlan) => {
  const targets = plan.envelope.operations.flatMap((operation) => operation.targetRefs)
  if (targets.length === 0) return ''
  const labels = targets.slice(0, 3).map((target) => target.title || target.id).join(', ')
  const extra = targets.length > 3 ? ` and ${targets.length - 3} more` : ''
  return `Touches ${labels}${extra}.`
}

const truncatePrompt = (prompt: string, max = 80) => {
  const cleaned = stripClarificationEnvelope(prompt).trim().replace(/\s+/g, ' ')
  if (cleaned.length <= max) return cleaned
  return `${cleaned.slice(0, max - 1).trimEnd()}…`
}

const buildClarificationCopy = (plan: AiOperationalPlan): string => {
  const prompt = truncatePrompt(plan.request.prompt)
  const scopeLine = summarizeTargets(plan)
  const opening = prompt
    ? `You want to: "${prompt}".`
    : `I have a fuzzy read on what you want next.`
  return [
    opening,
    scopeLine,
    `Do you want me to sketch this as a Manage-ready draft you can review, or should I just talk through what's on the workspace right now?`,
  ].filter(Boolean).join(' ')
}

const buildExecutePathCopy = (plan: AiOperationalPlan): string => {
  const prompt = truncatePrompt(plan.request.prompt)
  const scopeLine = summarizeTargets(plan)
  return [
    prompt ? `You want to: "${prompt}".` : 'I can take this in more than one direction.',
    scopeLine,
    'Should I do this once now, turn it into an automation, or keep it read-only and explain it?',
  ].filter(Boolean).join(' ')
}

const buildBlockCopy = (plan: AiOperationalPlan): string => {
  const prompt = truncatePrompt(plan.request.prompt)
  const preface = prompt ? `That request ("${prompt}") ` : `That request `
  return [
    `${preface}cannot execute from Line — it touches admin, economy, permissions, integrations, automation activation, or destructive workspace changes.`,
    `I can still help you draft a review-ready plan around it, or answer questions about the workspace without changing anything. Tell me which side to take.`,
  ].join(' ')
}

// Only surface the workspace context digest when the operation kind can
// actually use it. Meeting briefs and report previews stay clean; ambient
// pulse-summary answers still include it. Keeps BLOCKER pulse text out of
// user-facing meeting/report copy.
const DIGEST_RELEVANT_KINDS = new Set([
  'PULSE_SUMMARY',
  'GUIDED_TOUR',
  'NAVIGATION',
])

const localAnswerFor = (plan: AiOperationalPlan) => {
  const operation = plan.envelope.operations[0]
  const digest = plan.envelope.contextDigest?.summary?.trim()
  const summary = plan.envelope.simulation?.summary?.trim() ?? `Here's what I can see from the workspace, no changes made.`
  const scope = summarizeTargets(plan)
  const digestAllowed = operation?.kind ? DIGEST_RELEVANT_KINDS.has(operation.kind) : false
  return [
    summary,
    scope,
    digest && digestAllowed ? `Relevant context:\n${digest}` : '',
    operation?.kind === 'AUTOMATION_SUGGESTION'
      ? `I can outline automations as drafts only — turning them on still needs your review.`
      : '',
    operation?.kind === 'REPORT_PREVIEW'
      ? `I can preview a report without saving or publishing anything.`
      : '',
  ].filter(Boolean).join('\n\n')
}

const detectExecutePathIntent = (plan: AiOperationalPlan): 'RUN_NOW' | 'CREATE_AUTOMATION' | 'AMBIGUOUS' | null => {
  if (plan.request.surface !== 'AI_MAGIC' || plan.request.mode !== 'EXECUTE') return null
  if (plan.envelope.risk === 'RESTRICTED') return null
  const prompt = stripClarificationEnvelope(plan.request.prompt).trim()
  if (!prompt) return null
  if (EXECUTE_AUTOMATION_PROMPT_REGEX.test(prompt)) return 'CREATE_AUTOMATION'
  if (EXECUTE_RUN_NOW_PROMPT_REGEX.test(prompt)) return 'RUN_NOW'
  if (plan.envelope.risk === 'LOW') return null
  return 'AMBIGUOUS'
}

export const routeAiMagicOperationalPlan = (
  plan: AiOperationalPlan,
  confirmationResponse: AiMagicOperationalConfirmationResponse | null = null,
  options: {
    proposalAlreadyConfirmed?: boolean
    executePathResponse?: AiMagicExecutePathResponse | null
    suppressClarificationChips?: boolean
    createMode?: boolean
    detailedCreatePrompt?: boolean
  } = {},
): AiMagicOperationalRoute => {
  if (plan.nextStep === 'REFUSE' || plan.envelope.risk === 'RESTRICTED') {
    return { kind: 'BLOCK', message: buildBlockCopy(plan) }
  }

  if (isClarificationEnvelope(plan.request.prompt)) {
    return { kind: 'CONTINUE_MODEL' }
  }

  const isAiMagicCreateMode = options.createMode === true && plan.request.surface === 'AI_MAGIC'

  if (isAiMagicCreateMode && options.detailedCreatePrompt) {
    return { kind: 'CONTINUE_MODEL' }
  }

  if (isAiMagicCreateMode && plan.envelope.operations.some((operation) => operation.kind === 'AUTOMATION_SUGGESTION')) {
    return { kind: 'CONTINUE_MODEL' }
  }

  if (!isAiMagicCreateMode) {
    if (options.executePathResponse === 'EXPLAIN_ONLY') {
      return { kind: 'LOCAL_ANSWER', message: localAnswerFor(plan) }
    }

    if (options.executePathResponse === 'RUN_NOW') {
      return { kind: 'CONTINUE_MODEL', executionPath: 'RUN_NOW' }
    }

    if (options.executePathResponse === 'CREATE_AUTOMATION') {
      return { kind: 'CONTINUE_MODEL', executionPath: 'CREATE_AUTOMATION' }
    }

    const executePathIntent = detectExecutePathIntent(plan)
    if (executePathIntent === 'RUN_NOW') {
      return { kind: 'CONTINUE_MODEL', executionPath: 'RUN_NOW' }
    }
    if (executePathIntent === 'CREATE_AUTOMATION') {
      return { kind: 'CONTINUE_MODEL', executionPath: 'CREATE_AUTOMATION' }
    }
    if (executePathIntent === 'AMBIGUOUS') {
      if (options.suppressClarificationChips) {
        return { kind: 'CONTINUE_MODEL', escapeHint: EXPLAIN_ESCAPE_CHIP }
      }
      return {
        kind: 'CONFIRM',
        clarificationField: 'execute_path',
        message: buildExecutePathCopy(plan),
        quickControls: [
          { type: 'radio', group: 'execute-path', label: 'Do it now', value: 'Run this once now.' },
          { type: 'radio', group: 'execute-path', label: 'Create automation', value: 'Turn this into an automation workflow.' },
          { type: 'radio', group: 'execute-path', label: 'Explain only', value: 'Keep this read-only and explain the setup.' },
        ],
      }
    }
  }

  if (confirmationResponse === 'EXPLAIN_ONLY') {
    return { kind: 'LOCAL_ANSWER', message: localAnswerFor(plan) }
  }

  if (confirmationResponse === 'PREPARE_PROPOSAL' || options.proposalAlreadyConfirmed) {
    return { kind: 'CONTINUE_MODEL' }
  }

  if (plan.nextStep === 'CONFIRM') {
    const prompt = stripClarificationEnvelope(plan.request.prompt).trim()
    const hasExplicitDraftIntent = TIMELINE_DRAFT_INTENT_REGEX.test(prompt) || REVIEW_ONLY_DRAFT_REGEX.test(prompt)
    const hasClearIntentLeader = CLEAR_INTENT_LEADERS_REGEX.test(prompt)
    const hasSelectedTargets = (plan.request.selectedTargets ?? []).length > 0
    const isAiMagic = plan.request.surface === 'AI_MAGIC'
    if (isAiMagic && (plan.request.mode === 'PLAN' || plan.request.mode === 'EXECUTE') && (hasExplicitDraftIntent || hasClearIntentLeader) && !hasSelectedTargets) {
      return { kind: 'CONTINUE_MODEL' }
    }
    // Guess-then-offer: for ambiguous AI_MAGIC intents, let the model draft
    // its best-guess response and attach a one-tap escape chip instead of
    // stalling with a robotic "which do you want?" gate.
    if (isAiMagic && !hasSelectedTargets) {
      return { kind: 'CONTINUE_MODEL', escapeHint: EXPLAIN_ESCAPE_CHIP }
    }

    if (options.suppressClarificationChips) {
      return { kind: 'CONTINUE_MODEL', escapeHint: EXPLAIN_ESCAPE_CHIP }
    }

    return {
      kind: 'CONFIRM',
      clarificationField: 'operational_confirmation',
      message: buildClarificationCopy(plan),
      quickControls: [
        { type: 'radio', group: 'operational-confirmation', label: 'Prepare proposal', value: 'Prepare this as a reviewable proposal.' },
        { type: 'radio', group: 'operational-confirmation', label: 'Explain only', value: 'Keep this read-only and explain the workspace state.' },
      ],
    }
  }

  if (plan.envelope.risk === 'LOW' && (plan.nextStep === 'ANSWER' || plan.nextStep === 'SHOW_PLAN')) {
    return { kind: 'LOCAL_ANSWER', message: localAnswerFor(plan) }
  }

  return { kind: 'CONTINUE_MODEL' }
}
